import { SAVE_CHANGES_LABEL } from '@microsec/constants';
import { finalize } from 'rxjs';
import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { COMPLIANCE_STANDARDS, DEFAULT_SECURITY_LEVELS, FOUNDATIONAL_REQUIREMENT_LABELS } from '@ids-constants';
import { BaseComponent, CardComponent, CommonTableComponent, LoadingOverlayComponent } from '@microsec/components';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { BulkEditDevicesOverviewFormComponent } from './bulk-edit-devices-overview-form/bulk-edit-devices-overview-form.component';
import { FORM_PARAMS as DEVICE_FORM_PARAMS } from '../../../../../shared-components/shared-device-form/shared-device-form.component';
import { TargetDeviceService } from '@ids-services';
import { FormItem } from '@microsec/models';
import { ArrayMapPipe } from '@ids-pipes';
import { CommonModule } from '@angular/common';
import { TabsModule } from 'primeng/tabs';
import { ButtonModule } from 'primeng/button';
import { ComplianceDetailsFieldComponent } from '../../../../../shared-components/shared-device-form/compliance-details-field/compliance-details-field.component';
import { CheckboxModule } from 'primeng/checkbox';

const FORM_PARAMS = {
  IS_COMPLIANCE_NEEDED: 'is_compliance_needed',
  BULK_EDIT_FORM_DATA: 'bulk_edit_data',
};

const DISPLAY_COLS: any[] = ['label', 'type', 'interfaces', 'src_mac_addr', 'src_ip_addr', 'src_ble_addr', 'zones', 'status'];

@Component({
  selector: 'app-bulk-edit-devices-form',
  templateUrl: './bulk-edit-devices-form.component.html',
  styleUrls: ['./bulk-edit-devices-form.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TabsModule,
    CheckboxModule,
    ButtonModule,
    LoadingOverlayComponent,
    CardComponent,
    CommonTableComponent,
    ComplianceDetailsFieldComponent,
    BulkEditDevicesOverviewFormComponent,
  ],
  providers: [ArrayMapPipe],
})
export class BulkEditDevicesFormComponent extends BaseComponent implements OnInit {
  isLoading = false;

  activeTabIndex = 0;

  selectedDevices: any[] = [];

  securityLevel: any = {
    target: DEFAULT_SECURITY_LEVELS,
    capable: DEFAULT_SECURITY_LEVELS,
  };

  complianceDetailsValid = true;

  cellTemplate!: TemplateRef<any>;

  cols: any[] = [];

  form!: FormGroup;

  @ViewChild('bulkEditForm') bulkEditForm!: BulkEditDevicesOverviewFormComponent;

  FORM_PARAMS = FORM_PARAMS;

  SAVE_CHANGES_LABEL = SAVE_CHANGES_LABEL;

  constructor(
    private targetDeviceSrv: TargetDeviceService,
    private dialogConfig: DynamicDialogConfig,
    public dialogRef: DynamicDialogRef,
    private arrayPipe: ArrayMapPipe,
  ) {
    super();
  }

  async ngOnInit() {
    await this.prepareConfigs();
    this.selectedDevices = this.dialogConfig.data.devices;
    this.cellTemplate = this.dialogConfig.data.cellTemplate;
    this.cols = ((this.dialogConfig.data.allColumns as any[]) || []).filter((col: any) => DISPLAY_COLS.includes(col?.field));
    this.initForm();
  }

  initForm() {
    this.form = new FormGroup({
      [FORM_PARAMS.IS_COMPLIANCE_NEEDED]: new FormControl(false),
    });
    setTimeout(() => {
      this.form?.addControl(FORM_PARAMS.BULK_EDIT_FORM_DATA, this.bulkEditForm?.form?.form);
    });
  }

  onSubmit() {
    let complianceChanges = '';
    if (this.form.get(FORM_PARAMS.IS_COMPLIANCE_NEEDED)?.value) {
      complianceChanges = Object.values(FOUNDATIONAL_REQUIREMENT_LABELS).reduce((acc, curr, i) => {
        return acc + `<li>${curr}: <b>${this.securityLevel?.target?.[i]}</b> (target), <b>${this.securityLevel?.capable?.[i]}</b> (capability) </li>`;
      }, '');
    }
    const overviewModifiedPayload = this.getBulkEditOverviewPayload();
    const overviewChanges: string = this.bulkEditForm.fields.reduce((acc, field: FormItem) => {
      if (!!field.name && overviewModifiedPayload.hasOwnProperty(field.name)) {
        const change = overviewModifiedPayload[field.name];
        return (
          acc +
          `<li>${field.label}: ${
            !change || change.length === 0
              ? '<i>(None)</i>'
              : Array.isArray(change)
                ? `<b>${this.arrayPipe.transform(change, 'label')}</b>`
                : `<b>${change}</b>`
          }</li>`
        );
      }
      return acc;
    }, '');

    this.confirm({
      action: 'Review Bulk Edit Change(s)',
      acceptLabel: 'Confirm Changes',
      rejectLabel: 'Keep Editing',
      customContent: `Are you sure you want to update the selected devices with the following changes?
      <ul>
        ${!!overviewChanges ? overviewChanges : ''}
        ${
          complianceChanges
            ? !this.isComplianceEmpty
              ? `<li>Compliance Details:</li><ul>${complianceChanges}</ul>`
              : '<li>Compliance Details: <i>(None)</i></li>'
            : ''
        }
      </ul>
      <p>Fields with <i>(None)</i> will result in the deletion of the original values. These changes cannot be undone.</p>
     `,
      next: () => {
        this.isLoading = true;
        this.targetDeviceSrv
          .updateDevices(this.getPayload())
          .pipe(
            finalize(() => {
              this.isLoading = false;
              this.dialogRef.close(true);
            }),
          )
          .subscribe({
            next: (res: any) => {
              this.dialogRef.close(res);
            },
            error: (err: any) => {
              this.showErrorMessage(err);
            },
          });
      },
    });
  }

  getPayload() {
    const bulkEditPayload: Record<string, any> = this.getBulkEditOverviewPayload();

    const payload = {
      ['device_ids']: [...this.selectedDevices.map((device: any) => device.id)],
      ['update_fields']: {
        ...bulkEditPayload,
        ...(this.form.get(FORM_PARAMS.IS_COMPLIANCE_NEEDED)?.value
          ? this.isComplianceEmpty
            ? { compliance: null }
            : this.modifySecurityLevelPayload()
          : {}),
      },
    };
    return payload;
  }

  getBulkEditOverviewPayload() {
    const bulkEditData = this.form?.getRawValue()[FORM_PARAMS.BULK_EDIT_FORM_DATA];
    const bulkEditPayload: Record<string, any> = {};
    // Remove toggle from payload and removed if field toggle is false
    const bulkEditPayloadArr = Object.entries(bulkEditData).filter(([key]) => !key.includes('_toggle') && bulkEditData[`${key}_toggle`]);

    bulkEditPayloadArr.forEach(([key, value]) => {
      if (key === DEVICE_FORM_PARAMS.TAGS) {
        bulkEditPayload[key] = this.bulkEditForm?.selectedTags;
      } else if (key === DEVICE_FORM_PARAMS.ZONES) {
        bulkEditPayload[key] = this.bulkEditForm?.selectedZones;
      } else {
        bulkEditPayload[key] = value;
      }
    });

    return bulkEditPayload;
  }

  modifySecurityLevelPayload() {
    return {
      compliance: {
        [COMPLIANCE_STANDARDS.IEC_62443]: {
          security_level_target: this.securityLevel.target,
          security_level_capable: this.securityLevel.capable,
        },
      },
    };
  }

  toggleCompliance(event: any) {
    this.form.patchValue({ [FORM_PARAMS.IS_COMPLIANCE_NEEDED]: event.checked });
    this.complianceDetailsValid = event.checked;
  }

  get isValid() {
    const isComplianceNeeded = this.form.get(FORM_PARAMS.IS_COMPLIANCE_NEEDED)?.value;
    const bulkEditData = this.form?.getRawValue()[FORM_PARAMS.BULK_EDIT_FORM_DATA];
    const isOverviewChangeEmpty = ((Object.keys((bulkEditData as any[]) || []).filter((key) => key.includes('_toggle')) as string[]) || []).every(
      (key) => bulkEditData?.[key] === false,
    );
    // If no change in overview and no change in compliance detail, then form is invalid
    // If compliance detail box is checked and compliance detail is half way filled, then form is also invalid
    return (
      this.form.valid &&
      (!isOverviewChangeEmpty || !!isComplianceNeeded) &&
      (!isComplianceNeeded || !!this.isComplianceEmpty || !!this.complianceDetailsValid)
    );
  }

  resetValues() {
    switch (this.activeTabIndex) {
      case 1: {
        // Reset Overview Bulk Edit Form Page
        if (this.bulkEditForm?.form?.form) {
          const bulkEditDefaultData = Object.keys(this.bulkEditForm.form.form.controls).reduce(
            (acc, key) => {
              if (key.includes('_toggle')) {
                acc[key] = false;
              } else {
                if (key === DEVICE_FORM_PARAMS.ZONES || key === DEVICE_FORM_PARAMS.TAGS) {
                  acc[key] = [];
                } else if (key === DEVICE_FORM_PARAMS.TYPE) {
                  acc[key] = null;
                } else if (key === DEVICE_FORM_PARAMS.NETWORK_MAP_LEVEL) {
                  acc[key] = -1;
                } else if (key === DEVICE_FORM_PARAMS.CRITICALITY) {
                  acc[key] = 5;
                } else {
                  acc[key] = '';
                }
              }
              return acc;
            },
            {} as Record<string, any>,
          );
          this.form.patchValue({ [FORM_PARAMS.BULK_EDIT_FORM_DATA]: bulkEditDefaultData });
        }
        this.bulkEditForm.selectedTags = [];
        this.bulkEditForm.selectedZones = [];
        break;
      }
      case 2: {
        // Reset Compliance Bulk Edit Form Page
        this.toggleCompliance(false);
        this.securityLevel = {
          target: DEFAULT_SECURITY_LEVELS,
          capable: DEFAULT_SECURITY_LEVELS,
        };
        break;
      }
      default: {
        this.showErrorMessage('Reset Function is not available in this tab!');
        break;
      }
    }
  }

  private get isComplianceEmpty(): boolean {
    return ((Object.keys(this.securityLevel) as any[]) || []).reduce((acc, key) => {
      return acc && ((this.securityLevel[key] as number[]) || []).every((value) => value === null);
    }, true);
  }

  onCancel() {
    this.dialogRef.close();
  }
}
